import type {
  GamePlayerProjectionsRequest,
  Status
} from '@library/types/projection';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AutoTradeHistoryDrawer } from '@bo-components/autoTradeHistoryDrawer/AutoTradeHistoryDrawer';
import { BulkBoardStatusButton } from '@bo-components/trading/BulkStatusButton';
import { BulkBoardTimeButton } from '@bo-components/trading/BulkBoardTimeButton';
import { BulkStartTimeButton } from '@bo-components/trading/BulkStartTimeButton';
import { Button } from '@library/components/buttons';
import { ConfirmationModal } from '@library/components/modals/ConfirmationModal';
import { DEFAULT_TRADING_FILTERS } from '@bo-constants/filters';
import type { DateRange } from '@library/types/dates';
import { DateTime } from 'luxon';
import { GlobalFilters } from '@library/components/GlobalFilters';
import { LEAGUES_WITH_DISPLAY_TIME } from '@bo-constants/leagues';
import { LeagueDependentFilters } from '@library/components/LeagueDependentFilters';
import { Loading } from '@library/components/Loading';
import { NoResults } from '@library/components/NoResults';
import { PageTitle } from '@library/components/PageTitle';
import { PlayerProjectionsModal } from '@bo-components/playerModal/PlayerProjectionsModal';
import { QuickFiltersDrawer } from '@bo-components/trading/QuickFiltersDrawer';
import { RankDrawer } from '@bo-components/rankDrawer/RankDrawer';
import type { TradingFilters } from '@library/types/filters';
import { TradingGames } from '@bo-components/trading/games/TradingGames';
import drawer from '@library/assets/images/icons/drawer.svg';
import { getParameterValue } from '@library/component-utils/filters';
import { hasStandAloneOwnAttributes } from '@bo-utils/leagueAttributes';
import { removeDuplicatesInArray } from '@bo-utils/common';
import { selectLoggedInUserId } from '@bo-services/authenticationSlice';
import { useAutoTradeHistoryDrawerProps } from '@bo-hooks/autoTradeHistoryDrawer/useAutoTradeHistoryDrawerProps';
import { useConfirmationModal } from '@bo-hooks/useConfirmationModal';
import { useDashBoard } from '@bo-hooks/useDashboard';
import { useGames } from '@bo-hooks/useGames';
import { useGetProjectionsQuery } from '@bo-services/tradingApi';
import { usePlayerModal } from '@bo-hooks/playerModal/usePlayerModal';
import { useSelector } from 'react-redux';
import { useSentrySetDashBoardTag } from '@bo-hooks/sentry/useSentrySetDashBoardTag';
import { useUpdateBulkProjectionsMutation } from '@bo-services/projectionsApi';
import { withProfiler } from '@sentry/react';

const TradingDashboardView = () => {
  useSentrySetDashBoardTag({ dashboardName: 'Trading' });

  const {
    bulkLeagueIdGroups,
    changeFilterValue,
    changeFilterValues,
    durations,
    filters,
    handleMultiSelectDurations,
    handleSearch,
    handleSelectDateRange,
    handleSelectLeague,
    isLeagueError,
    isLeagueLoading,
    leagueInfo,
    leaguesList,
    searchValue,
    selectedDurations,
    selectedEndDate,
    selectedLeaguesDurationAttributes,
    selectedStartDate,
    setFilters
  } = useDashBoard({
    dashboardName: 'TRADING',
    initialStartDate: DateTime.now().startOf('day'),
    initialEndDate: DateTime.now().endOf('day')
  });

  const { id: baseLeagueId, name } = leagueInfo ?? {};

  const isDisplayTimeVisible = useMemo(
    () => LEAGUES_WITH_DISPLAY_TIME.includes(name?.toUpperCase() ?? ''),
    [name]
  );

  const {
    demonsAndGoblins: selectedDemonsAndGoblins,
    games: selectedGames,
    gameModes: selectedGameModeIds,
    inGame: selectedInGame,
    statType: selectedStatType,
    status: selectedStatus,
    teams: selectedTeams
  } = filters as TradingFilters;

  const statTypes = useMemo(
    () =>
      selectedLeaguesDurationAttributes
        .map((leagueDurationAttributes) => leagueDurationAttributes.statTypes)
        .flat(),
    [selectedLeaguesDurationAttributes]
  );

  const allStats = useMemo(
    () => removeDuplicatesInArray(statTypes),
    [statTypes]
  );

  const standAloneAttributes = useMemo(
    () => hasStandAloneOwnAttributes({ name }),
    [name]
  );
  const leagueId = useMemo(
    () =>
      standAloneAttributes
        ? [baseLeagueId ?? '']
        : selectedLeaguesDurationAttributes.map(
            (leagueDurationAttributes) => leagueDurationAttributes.leagueId
          ),
    [baseLeagueId, selectedLeaguesDurationAttributes, standAloneAttributes]
  );

  const infScrollRef = useRef(null);
  const [page, setPage] = useState(1);

  const startDateString = useMemo(
    () => selectedStartDate.toISO() || '',
    [selectedStartDate]
  );
  const endDateString = useMemo(
    () => selectedEndDate.toISO() || '',
    [selectedEndDate]
  );

  const handleChangeDate = (value: DateRange) => {
    handleSelectDateRange(value);
    setFilters(DEFAULT_TRADING_FILTERS);
    setPage(1);
  };

  const handleChangeLeague = (newLeagueId: string) => {
    handleSelectLeague(newLeagueId);
    setFilters(DEFAULT_TRADING_FILTERS);
    setPage(1);
  };

  const handleReset = () => {
    setFilters(DEFAULT_TRADING_FILTERS);
    handleSearch('');
    setPage(1);
  };

  const handleDemonsAndGoblinsChange = (value: string) => {
    changeFilterValue('demonsAndGoblins', value);
    setPage(1);
  };

  const handleGamesChange = (value: Array<string>) => {
    changeFilterValue('games', value);
    setPage(1);
  };

  const handleStatTypeChange = (value: Array<string>) => {
    changeFilterValue('statType', value);
    setPage(1);
  };

  const handleStatusChange = (value: Array<string>) => {
    changeFilterValue('status', value);
    setPage(1);
  };

  const handleTeamsChange = (value: Array<string>) => {
    changeFilterValue('teams', value);
    setPage(1);
  };

  const handleGameModesChange = (value: Array<string>) => {
    changeFilterValue('gameModes', value);
    setPage(1);
  };

  const handleMultiSelectDurationsChange = (value: Array<string>) => {
    handleMultiSelectDurations(value);
    setPage(1);
  };

  const handleSearchChange = (value: string) => {
    handleSearch(value);
    setPage(1);
  };

  const handleChangeLeagueChange = (newLeagueId: string) => {
    handleChangeLeague(newLeagueId);
    setPage(1);
  };

  const handleInGameChange = (value: string) => {
    changeFilterValue('inGame', value);
    setPage(1);
  };

  const handleApplyFilterSet = useCallback(
    (filterSet: string) => {
      const ptsStat = allStats.find(
        (stat) => stat.leagueProjectionFilterName === 'Points'
      );
      switch (filterSet) {
        case 'PRE_GAME_DG':
          changeFilterValues({
            demonsAndGoblins: 'true',
            status: ['pre_game']
          });
          setPage(1);
          break;
        case 'REMOVED_POINTS':
          changeFilterValues({
            statType: [ptsStat?.leagueProjectionFilterId || ''],
            status: ['removed']
          });
          setPage(1);
          break;
        case 'TODAYS_GAMES':
          handleSelectDateRange({
            fromDate: DateTime.now().startOf('day'),
            toDate: DateTime.now().endOf('day')
          });
          setFilters(DEFAULT_TRADING_FILTERS);
          setPage(1);
          break;
        default:
          break;
      }
    },
    [allStats, changeFilterValues, handleSelectDateRange, setFilters]
  );

  // Sending the parent/base leagueId since games are same across the leagues/sub leagues
  // this would avoid multiple api calls when the duration/sub league is changed
  const { games } = useGames({
    leagueId: baseLeagueId,
    fromDate: startDateString,
    toDate: endDateString
  });

  const loggedInUserId = useSelector(selectLoggedInUserId);

  const tradingQueryParams = useMemo<GamePlayerProjectionsRequest>(() => {
    return {
      demonsAndGoblins: getParameterValue(selectedDemonsAndGoblins),
      createdByUserId: window.backoffice?.isAutomatedTesting
        ? Number(loggedInUserId)
        : undefined,
      durationId: standAloneAttributes ? selectedDurations : [],
      leagueId,
      fromDate: startDateString,
      gameId: getParameterValue(selectedGames),
      gameModeIds: getParameterValue(selectedGameModeIds),
      inGame: getParameterValue(selectedInGame),
      search: searchValue,
      statTypeIds: getParameterValue(selectedStatType),
      status: getParameterValue<Array<Status>>(selectedStatus),
      team: getParameterValue(selectedTeams),
      toDate: endDateString
    };
  }, [
    endDateString,
    leagueId,
    loggedInUserId,
    searchValue,
    selectedDemonsAndGoblins,
    selectedDurations,
    selectedGameModeIds,
    selectedGames,
    selectedInGame,
    selectedStatType,
    selectedStatus,
    selectedTeams,
    standAloneAttributes,
    startDateString
  ]);

  const {
    data: tradingData,
    isLoading,
    isFetching,
    isError
  } = useGetProjectionsQuery(
    {
      ...tradingQueryParams,
      page
    },
    {
      skip: leagueId.length === 0
    }
  );

  const [disabledFilters, setDisabledFilters] = useState(false);

  const isLoadingPlayers = isLoading || isFetching;

  const {
    boardTime,
    description,
    durationId,
    foreignId,
    gameId,
    gameStartTime,
    handleSelectPlayerModal,
    onCancelPlayerModal,
    playerId,
    playerDescriptors,
    position,
    showPlayerModal,
    startTime,
    team,
    teamName
  } = usePlayerModal();

  const {
    drawerOpen: autoTradeDrawerOpen,
    durationId: autoTradeDurationId,
    gameDescriptor: autoTradeGameDescriptor,
    gameId: autoTradeGameId,
    handleSelectAutoTradeHistory,
    leagueId: autoTradeLeagueId,
    onCancelAutoTradeHistoryDrawerModal,
    playerId: autoTradePlayerId,
    playerImage: autoTradePlayerImage,
    playerName: autoTradePlayerName,
    position: autoTradePosition
  } = useAutoTradeHistoryDrawerProps();

  const [rankDrawerOpen, setRankDrawerOpen] = useState(false);
  const [quickFiltersDrawerOpen, setQuickFiltersDrawerOpen] = useState(false);

  const handleOpenDrawer = () => setQuickFiltersDrawerOpen(true);

  const {
    count,
    estimatedTotal,
    tradingGames: tradingGamesData
  } = tradingData ?? {};

  const {
    confirmationModalProps,
    handleSubmit,
    resetConfirmationModal,
    setConfirmationModalProps,
    updateConfirmationModalProp
  } = useConfirmationModal();

  const tradingGames = useMemo(
    () => tradingGamesData ?? {},
    [tradingGamesData]
  );

  const isNoData =
    isLeagueError ||
    !leagueInfo ||
    !tradingGames ||
    Object.keys(tradingGames).length === 0 ||
    leagueId.length === 0 ||
    isError;

  useEffect(() => {
    const nextPage = () => {
      setPage(page + 1);
    };

    const ref = infScrollRef;

    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (Number(count) < Number(estimatedTotal) && target.isIntersecting) {
          nextPage();
        }
      },
      { root: null, threshold: 0.9 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [infScrollRef, count, estimatedTotal, isLoadingPlayers, isNoData, page]);

  const [bulkUpdateProjections, { isLoading: isBulkActionLoading }] =
    useUpdateBulkProjectionsMutation();

  const showInfScrollRef =
    !isLoadingPlayers &&
    Object.keys(tradingGames).length > 0 &&
    Number(count) < Number(estimatedTotal);

  const estPrefix = count !== estimatedTotal ? '~' : '';
  const countResults =
    !!count && !!estimatedTotal
      ? `${count} of ${estPrefix}${estimatedTotal} Results`
      : '';

  if (isLeagueLoading) {
    return <Loading loadingMessage="Loading Leagues" />;
  }

  return (
    <div className="w-auto px-6 pb-6 pt-3">
      <PageTitle title="Trading" />
      <div className="flex flex-col gap-4 pt-4">
        <div className="flex gap-14">
          <GlobalFilters
            allowPastDates
            className="flex w-full gap-4"
            disabled={disabledFilters}
            durations={durations}
            handleMultiSelectDurations={handleMultiSelectDurationsChange}
            handleSearch={handleSearchChange}
            handleSelectDateRange={handleChangeDate}
            handleSelectLeague={handleChangeLeagueChange}
            leaguesList={leaguesList}
            searchDisabled={disabledFilters}
            searchValue={searchValue}
            searchPlaceholder="Search"
            selectedStartDate={selectedStartDate}
            selectedEndDate={selectedEndDate}
            selectedDurations={selectedDurations}
            selectedLeagueId={baseLeagueId}
          />
          <Button
            className="text-soClean-100 bg-soFresh-110 flex h-11 w-32 items-center justify-center rounded-md text-sm"
            onClick={() => setRankDrawerOpen(true)}
          >
            <span className="pr-2">Global Rank</span>
            <img alt="open Global Rank drawer" src={drawer} />
          </Button>
        </div>

        <div className="flex w-full items-center gap-4">
          <LeagueDependentFilters
            className="flex flex-wrap items-center gap-4"
            disabled={disabledFilters}
            filters={filters}
            games={games}
            handleDemonsAndGoblinsChange={handleDemonsAndGoblinsChange}
            handleGameModesChange={handleGameModesChange}
            handleGamesChange={handleGamesChange}
            handleInGameChange={handleInGameChange}
            handleStatTypeChange={handleStatTypeChange}
            handleStatusChange={handleStatusChange}
            handleTeamsChange={handleTeamsChange}
            onReset={handleReset}
            statTypeValues={allStats}
            showDrawer
            handleOpenDrawer={handleOpenDrawer}
          />

          <div className="grow" />

          <div className="flex items-start gap-4">
            <BulkBoardStatusButton
              bulkUpdateProjections={bulkUpdateProjections}
              setConfirmationModalProps={setConfirmationModalProps}
              tradingQueryParams={tradingQueryParams}
            />
            <BulkBoardTimeButton
              bulkUpdateProjections={bulkUpdateProjections}
              setConfirmationModalProps={setConfirmationModalProps}
              tradingQueryParams={tradingQueryParams}
            />
            <BulkStartTimeButton
              bulkUpdateProjections={bulkUpdateProjections}
              setConfirmationModalProps={setConfirmationModalProps}
              tradingQueryParams={tradingQueryParams}
            />
            <span className="font-heebo text-soClean-100 whitespace-nowrap py-3 text-sm font-bold">
              {countResults}
            </span>
          </div>
        </div>
      </div>
      <div className="col-span-10">
        {leagueInfo && (
          <TradingGames
            bulkLeagueIdGroups={bulkLeagueIdGroups}
            disabledFilters={disabledFilters}
            handleShowAutoTradeDrawer={handleSelectAutoTradeHistory}
            initialTradingGames={tradingGames}
            leagueInfo={leagueInfo}
            games={games}
            handleSelectPlayerModal={handleSelectPlayerModal}
            setConfirmationModalProps={setConfirmationModalProps}
            setDisabledFilters={setDisabledFilters}
            updateConfirmationModalProp={updateConfirmationModalProp}
            isDisplayTimeVisible={isDisplayTimeVisible}
          />
        )}
        {isLoadingPlayers && (
          <Loading loadingMessage="Loading Players and Projections" />
        )}
      </div>
      {!isLoadingPlayers && isNoData && <NoResults />}
      {showInfScrollRef && <div ref={infScrollRef} />}
      {showPlayerModal && (
        <PlayerProjectionsModal
          durationId={durationId}
          isOpen={showPlayerModal}
          boardTime={boardTime}
          description={description}
          foreignId={foreignId}
          gameId={gameId}
          gameStartTime={gameStartTime}
          onCancelModal={onCancelPlayerModal}
          leagueInfo={leagueInfo}
          playerId={playerId}
          playerDescriptors={playerDescriptors}
          position={position}
          startTime={startTime}
          team={team}
          teamName={teamName}
          isDisplayTimeVisible={isDisplayTimeVisible}
        />
      )}
      {confirmationModalProps && (
        <ConfirmationModal
          confirmationCopy={confirmationModalProps.confirmationCopy}
          onCancelModal={resetConfirmationModal}
          handleConfirm={handleSubmit}
          label={confirmationModalProps.label}
          isOpen
          isConfirmDisabled={
            confirmationModalProps.isConfirmDisabled || isBulkActionLoading
          }
          isLoading={isBulkActionLoading}
        >
          {confirmationModalProps.children}
        </ConfirmationModal>
      )}
      <RankDrawer
        drawerOpen={rankDrawerOpen}
        fromDateString={startDateString}
        initialStatType={selectedStatType[0]}
        onCancelDrawer={() => setRankDrawerOpen(false)}
        leagueId={leagueId}
        leagueInfo={leagueInfo}
        statTypes={allStats}
        games={games}
      />
      <AutoTradeHistoryDrawer
        drawerOpen={autoTradeDrawerOpen}
        durationId={autoTradeDurationId}
        fromDateString={startDateString}
        gameDescriptor={autoTradeGameDescriptor}
        gameId={autoTradeGameId}
        leagueId={autoTradeLeagueId}
        onCancelDrawer={onCancelAutoTradeHistoryDrawerModal}
        playerId={autoTradePlayerId}
        playerImage={autoTradePlayerImage}
        playerName={autoTradePlayerName}
        position={autoTradePosition}
      />
      <QuickFiltersDrawer
        drawerOpen={quickFiltersDrawerOpen}
        handleCancelDrawer={() => setQuickFiltersDrawerOpen(false)}
        handleApplyFilterSet={handleApplyFilterSet}
      />
    </div>
  );
};

export const TradingDashboard = withProfiler(TradingDashboardView);
